import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';

interface PartNumberSuggestion {
  value: string;
  suggestion: string | null;
  reason: string;
  count: number;
  sheets: string[];
}

const SUGGESTIONS_PREVIEW = 4;

@Component({
  selector: 'app-show-web-socket-response',
  template: `
    <ng-container *ngIf="data">
      <ng-container *ngIf="hasErrors; else infoHeader">
        <div class="dlg-h">
          <span class="ico ico-error">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </span>
          <div class="dlg-htext">
            <div class="dlg-title">Upload failed</div>
            <div class="dlg-sub" *ngIf="fileName">{{ fileName }}</div>
          </div>
        </div>
      </ng-container>
      <ng-template #infoHeader>
        <ng-container *ngIf="suggestions.length; else warnHeader">
          <div class="dlg-h">
            <span class="ico ico-ok">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                   stroke="currentColor" stroke-width="2.2"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
            </span>
            <div class="dlg-htext">
              <div class="dlg-title">File uploaded successfully</div>
              <div class="dlg-sub" *ngIf="fileName">
                {{ fileName }} · all sheets validated ·
                {{ suggestions.length }} value{{ suggestions.length > 1 ? 's' : '' }} worth a look
              </div>
            </div>
          </div>
        </ng-container>
        <ng-template #warnHeader>
          <div class="dlg-h">
            <span class="ico ico-warn">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                   stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 9v4"/>
                <path d="M12 17h.01"/>
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/>
              </svg>
            </span>
            <div class="dlg-htext">
              <div class="dlg-title">Completed with warnings</div>
              <div class="dlg-sub" *ngIf="fileName">{{ fileName }}</div>
            </div>
          </div>
        </ng-template>
      </ng-template>

      <mat-dialog-content class="mat-typography dlg-b">
        <!-- ERROR SECTION -->
        <ng-container *ngIf="hasErrors">
          <p class="dlg-lead">
            This file couldn't be processed. Fix the issues below and re-upload:
          </p>
          <ul class="reasons reasons-error">
            <ng-container *ngIf="fileEntry[fileName]">
              <li *ngFor="let error of fileEntry[fileName]">{{ error }}</li>
            </ng-container>
            <ng-container *ngFor="let item of errors | keyvalue">
              <li class="reason-group-label">{{ item.key }}</li>
              <li *ngFor="let error of item.value">{{ error }}</li>
            </ng-container>
          </ul>
        </ng-container>

        <!-- PART-NUMBER SUGGESTIONS (suggestive, never definitive) -->
        <ng-container *ngIf="suggestions.length">
          <p class="sug-lead" [class.dlg-lead-spaced]="hasErrors">
            A few <b>Commercial Part Number</b> values <i>may</i> include extra text such as
            a manufacturer name. If so, DigiKey won't recognise them and those parts keep
            their last known price. Only you can tell — here's what we noticed:
          </p>
          <div class="sug" *ngFor="let s of visibleSuggestions">
            <div class="sug-line1">
              <span class="sug-pn">{{ s.value }}</span>
              <span class="sug-cnt">{{ s.count }} cell{{ s.count > 1 ? 's' : '' }}</span>
            </div>
            <div class="sug-line2" *ngIf="s.suggestion; else noSuggestion">
              May include the manufacturer's name — did you mean
              <span class="sug-maybe">{{ s.suggestion }}
                <span class="sug-copy" matTooltip="Copy" (click)="copy(s.suggestion)">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="11" height="11" rx="2"/>
                    <path d="M5 15V5a2 2 0 0 1 2-2h10"/>
                  </svg>
                </span>
              </span>?
            </div>
            <ng-template #noSuggestion>
              <div class="sug-line2">
                Doesn't look like a part number — it may be a note that ended up in this column.
              </div>
            </ng-template>
            <div class="sug-line3">
              <span class="sug-sheet" *ngFor="let sheet of s.sheets">{{ sheet }}</span>
            </div>
          </div>
          <button
            type="button"
            class="sug-more"
            *ngIf="suggestions.length > SUGGESTIONS_PREVIEW"
            (click)="showAll = !showAll"
          >
            {{ showAll ? 'Show fewer' : 'Show ' + (suggestions.length - SUGGESTIONS_PREVIEW) + ' more suggestion' + (suggestions.length - SUGGESTIONS_PREVIEW > 1 ? 's' : '') }}
          </button>
          <div class="sug-note">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
            <span><b>Suggestions only</b> — if these values are already correct, no action is
            needed. Otherwise update the Export file and upload it again.</span>
          </div>
        </ng-container>

        <!-- OTHER INFO / WARNING SECTION (plain string warnings from other validators) -->
        <ng-container *ngIf="!isObjectEmpty(plainInfo)">
          <p
            class="dlg-lead dlg-lead-warn"
            [class.dlg-lead-spaced]="hasErrors || suggestions.length"
          >
            Warnings
          </p>
          <ul class="reasons reasons-warn">
            <ng-container *ngFor="let item of plainInfo | keyvalue">
              <li class="reason-group-label reason-group-label-warn">{{ item.key }}</li>
              <li *ngFor="let infoValue of item.value">{{ infoValue }}</li>
            </ng-container>
          </ul>
        </ng-container>
      </mat-dialog-content>

      <mat-dialog-actions align="end" class="dlg-f">
        <button type="button" class="btn-ghost" [mat-dialog-close]="false">Close</button>
        <button
          type="button"
          class="btn-primary"
          *ngIf="hasErrors"
          [mat-dialog-close]="'reupload'"
        >
          Re-upload
        </button>
      </mat-dialog-actions>
    </ng-container>
  `,
  styleUrls: ['./show-web-socket-response.component.scss'],
})
export class ShowWebSocketResponseComponent implements OnInit {
  readonly SUGGESTIONS_PREVIEW = SUGGESTIONS_PREVIEW;

  showResponse: any = [];
  fileEntry: { [key: string]: string[] } = {};
  errors: { [key: string]: string[] } = {};
  plainInfo: { [key: string]: string[] } = {};
  suggestions: PartNumberSuggestion[] = [];
  showAll = false;

  fileName = '';

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {
  }

  get hasErrors(): boolean {
    return !this.isObjectEmpty(this.errors) || !this.isObjectEmpty(this.fileEntry);
  }

  get visibleSuggestions(): PartNumberSuggestion[] {
    return this.showAll ? this.suggestions : this.suggestions.slice(0, SUGGESTIONS_PREVIEW);
  }

  ngOnInit(): void {
    this.fileName = this.data?.file_name || '';
    const info = this.data?.data?.info || {};
    this.errors = this.data?.data?.failure || {};
    if (!this.isObjectEmpty(this.errors[this.fileName])) {
      this.fileEntry = {
        [this.fileName]: this.errors[this.fileName] ?? [],
      };
      this.errors = Object.fromEntries(
        Object.entries(this.errors).filter(([key]) => key !== this.fileName)
      );
    }
    this.splitInfo(info);
  }

  /** Separate structured part-number payloads from plain string warnings,
   *  merging identical values across sheets. */
  private splitInfo(info: { [key: string]: any[] }): void {
    const merged = new Map<string, PartNumberSuggestion>();
    const plain: { [key: string]: string[] } = {};
    for (const [group, entries] of Object.entries(info || {})) {
      for (const entry of entries || []) {
        if (this.isPartNumberSuggestion(entry)) {
          for (const s of entry.suggestions || []) {
            this.mergeSuggestion(merged, s, entry.sheet);
          }
        } else {
          (plain[group] = plain[group] || []).push(String(entry));
        }
      }
    }
    this.suggestions = Array.from(merged.values()).sort((a, b) => b.count - a.count);
    this.plainInfo = plain;
  }

  private isPartNumberSuggestion(entry: any): boolean {
    return (
      !!entry &&
      typeof entry === 'object' &&
      entry.type === 'part_number_suggestions'
    );
  }

  /** Fold one suggestion into the merge map, tracking which sheets it came from. */
  private mergeSuggestion(
    merged: Map<string, PartNumberSuggestion>,
    s: any,
    sheet: string | undefined
  ): void {
    // Key on the pair, not a joined string: both halves can contain spaces, so
    // "A B"+"C" and "A"+"B C" would otherwise merge into one suggestion.
    const key = JSON.stringify([s.value, s.suggestion ?? '']);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, {
        value: s.value,
        suggestion: s.suggestion ?? null,
        reason: s.reason,
        count: s.count,
        sheets: sheet ? [sheet] : [],
      });
      return;
    }
    existing.count += s.count;
    if (sheet && !existing.sheets.includes(sheet)) {
      existing.sheets.push(sheet);
    }
  }

  copy(value: string | null): void {
    if (value) {
      navigator.clipboard?.writeText(value);
    }
  }

  isObjectEmpty(data: any): boolean {
    return (
      data === undefined ||
      (typeof data === 'object' &&
        data !== null &&
        Object.keys(data).length === 0)
    );
  }

}
